import { Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import { z } from 'zod';

import { CompanyProfile } from 'models';

import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { TextField } from 'components/forms/fields/TextField';
import { ModalTitle } from 'components/modals/ModalTitle';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalNotificationService } from 'services/LocalNotificationService';

type Values = {
    email: string;
};

const schema = z.object({
    email: z.string().trim().email(),
});

const RequestRemovalModal = ({ context, id, innerProps }: ContextModalProps<{ company: CompanyProfile }>) => {
    const { company } = innerProps;

    const submit = async ({ email }: Values) => {
        InternalTrackingService.track(
            'profile.requestRemoval',
            { profile: company.id, email },
            {
                notifySlack: true,
                includeAnonymous: true,
                details: [`🚫 Removal requested for *${company.name}* by ${email}`],
            },
        );

        LocalNotificationService.showSuccess({
            message: 'Thanks, we received your removal request and will get back to you soon.',
        });

        context.closeModal(id);
    };

    return (
        <Stack gap="xs" p="sm">
            <ModalTitle>Request removal</ModalTitle>
            <Text size="sm" c="dimmed" ta="center">
                Leave your email address and we will get in touch about removing the {company.name} profile.
            </Text>
            <Form<Values> defaultValues={{ email: '' }} zodSchema={schema} onSubmit={submit}>
                <Stack gap="xs">
                    <TextField name="email" type="email" label="Email" placeholder="you@company.com" required />
                    <FormSubmit>Submit request</FormSubmit>
                </Stack>
            </Form>
        </Stack>
    );
};

export { RequestRemovalModal };
